// src/pages/HPRevenueControlSentry.jsx
// HP Revenue Control Sentry - Edge Control Plane for Print Services
// Revenue-Focused Fleet Operations • Predictive Impact Analysis
// Celerway Edge AI Platform

import React, { useState, useEffect, useCallback } from 'react';
import ReactFlow, {
  Background,
  Controls,
  MiniMap,
  useNodesState,
  useEdgesState,
  Panel,
  Handle,
  Position,
} from 'reactflow';
import 'reactflow/dist/style.css';

// ════════════════════════════════════════════════════════════════════════════
// MOCK SITE DATA - Enterprise Print Fleet
// ════════════════════════════════════════════════════════════════════════════

const MOCK_SITE_GATEWAY = {
  id: 'gateway-hq-london',
  name: 'London HQ Print Hub',
  location: 'Canary Wharf Tower',
  status: 'Online',
  printerCount: 12,
  revenueAtRisk: 12400,
  revenueProtected: 847500,
  stratusModel: 'Stratus 5G Enterprise',
  connectivity: '5G + Ethernet',
  lastSync: new Date().toISOString(),
  monthlyPrintRevenue: 156000,
  slaCompliance: 99.2,
};

const MOCK_PRINTERS = [
  // Enterprise MFPs
  {
    id: 'MFP-LOBBY-01',
    type: 'Enterprise MFP',
    category: 'mfp',
    model: 'HP LaserJet Enterprise MFP M635',
    location: 'Main Lobby',
    floor: 'Ground',
    status: 'warning',
    ipAddress: '192.168.10.101',
    serialNumber: 'CNBKM12345',
    revenuePerDay: 2850,
    contractValue: 125000,
    metrics: {
      tonerBlack: 3,      // CRITICAL - triggers revenue impact
      tonerCyan: 45,
      tonerMagenta: 52,
      tonerYellow: 38,
      paperLevel: 75,
      pagesThisMonth: 12450,
      avgPagesPerDay: 415,
      temperature: 42,
      uptime: 99.2,
      networkLatency: 12,
    },
    slaWindow: { start: '08:00', end: '18:00' },
    businessCriticality: 'high',
    connectivity: 'Online',
  },
  {
    id: 'MFP-EXEC-01',
    type: 'Enterprise MFP',
    category: 'mfp',
    model: 'HP Color LaserJet Enterprise MFP M776',
    location: 'Executive Suite',
    floor: '12th',
    status: 'critical',
    ipAddress: '192.168.10.102',
    serialNumber: 'CNBKM12346',
    revenuePerDay: 4200,
    contractValue: 185000,
    metrics: {
      tonerBlack: 28,
      tonerCyan: 15,
      tonerMagenta: 8,    // LOW
      tonerYellow: 22,
      paperLevel: 25,     // LOW
      fuserLife: 12,      // CRITICAL
      pagesThisMonth: 8920,
      avgPagesPerDay: 297,
      jamRate: 2.8,       // HIGH
      temperature: 58,    // HIGH
      uptime: 94.1,
      networkLatency: 8,
    },
    slaWindow: { start: '07:00', end: '20:00' },
    businessCriticality: 'critical',
    connectivity: 'Online',
  },
  {
    id: 'MFP-FLOOR3-01',
    type: 'Enterprise MFP',
    category: 'mfp',
    model: 'HP LaserJet Enterprise MFP M528',
    location: 'Floor 3 Print Room',
    floor: '3rd',
    status: 'operational',
    ipAddress: '192.168.10.103',
    serialNumber: 'CNBKM12347',
    revenuePerDay: 3100,
    contractValue: 95000,
    metrics: {
      tonerBlack: 67,
      paperLevel: 90,
      pagesThisMonth: 18750,
      avgPagesPerDay: 625,
      temperature: 38,
      uptime: 99.8,
      networkLatency: 15,
    },
    slaWindow: { start: '08:00', end: '18:00' },
    businessCriticality: 'medium',
    connectivity: 'Online',
  },
  {
    id: 'MFP-FLOOR5-01',
    type: 'Enterprise MFP',
    category: 'mfp',
    model: 'HP Color LaserJet Enterprise MFP M681',
    location: 'Floor 5 Hub',
    floor: '5th',
    status: 'protected', // AI intervened
    ipAddress: '192.168.10.104',
    serialNumber: 'CNBKM12348',
    revenuePerDay: 2750,
    contractValue: 110000,
    metrics: {
      tonerBlack: 54,
      tonerCyan: 61,
      tonerMagenta: 58,
      tonerYellow: 49,
      paperLevel: 85,
      pagesThisMonth: 15230,
      avgPagesPerDay: 508,
      temperature: 40,
      uptime: 99.5,
      networkLatency: 22,
    },
    slaWindow: { start: '08:00', end: '18:00' },
    businessCriticality: 'medium',
    aiIntervention: {
      type: 'TRAFFIC_REROUTE',
      reason: 'Network degradation detected',
      timestamp: new Date(Date.now() - 1800000).toISOString(),
      revenueProtected: 8250,
    },
    connectivity: 'Online',
  },
  // Workgroup Printers
  {
    id: 'PR-FINANCE-01',
    type: 'Workgroup Printer',
    category: 'workgroup',
    model: 'HP LaserJet Pro M404dn',
    location: 'Finance Department',
    floor: '7th',
    status: 'warning',
    ipAddress: '192.168.10.201',
    serialNumber: 'CNBKM22345',
    revenuePerDay: 1850,
    contractValue: 45000,
    metrics: {
      tonerBlack: 18,     // LOW
      paperLevel: 45,
      pagesThisMonth: 28450,
      avgPagesPerDay: 948,
      temperature: 44,
      uptime: 98.9,
      networkLatency: 18,
    },
    slaWindow: { start: '08:00', end: '19:00' },
    businessCriticality: 'high',
    connectivity: 'Online',
  },
  {
    id: 'PR-HR-01',
    type: 'Workgroup Printer',
    category: 'workgroup',
    model: 'HP LaserJet Pro M404n',
    location: 'HR Department',
    floor: '6th',
    status: 'operational',
    ipAddress: '192.168.10.202',
    serialNumber: 'CNBKM22346',
    revenuePerDay: 980,
    contractValue: 32000,
    metrics: {
      tonerBlack: 72,
      paperLevel: 80,
      pagesThisMonth: 4250,
      avgPagesPerDay: 142,
      temperature: 36,
      uptime: 99.9,
      networkLatency: 10,
    },
    slaWindow: { start: '09:00', end: '17:00' },
    businessCriticality: 'low',
    connectivity: 'Online',
  },
  {
    id: 'PR-LEGAL-01',
    type: 'Workgroup Printer',
    category: 'workgroup',
    model: 'HP Color LaserJet Pro M454dw',
    location: 'Legal Department',
    floor: '8th',
    status: 'operational',
    ipAddress: '192.168.10.203',
    serialNumber: 'CNBKM22347',
    revenuePerDay: 1450,
    contractValue: 52000,
    metrics: {
      tonerBlack: 45,
      tonerCyan: 52,
      tonerMagenta: 48,
      tonerYellow: 55,
      paperLevel: 70,
      pagesThisMonth: 6780,
      avgPagesPerDay: 226,
      temperature: 39,
      uptime: 99.4,
      networkLatency: 14,
    },
    slaWindow: { start: '08:00', end: '20:00' },
    businessCriticality: 'medium',
    connectivity: 'Online',
  },
  // Production Printers
  {
    id: 'PROD-MAILROOM-01',
    type: 'Production Printer',
    category: 'production',
    model: 'HP PageWide Enterprise 586',
    location: 'Mailroom',
    floor: 'Ground',
    status: 'operational',
    ipAddress: '192.168.10.301',
    serialNumber: 'CNBKM32345',
    revenuePerDay: 5200,
    contractValue: 215000,
    metrics: {
      tonerBlack: 62,
      tonerCyan: 58,
      tonerMagenta: 61,
      tonerYellow: 54,
      paperLevel: 95,
      pagesThisMonth: 35200,
      avgPagesPerDay: 1173,
      temperature: 41,
      uptime: 99.1,
      networkLatency: 11,
    },
    slaWindow: { start: '06:00', end: '22:00' },
    businessCriticality: 'critical',
    connectivity: 'Online',
  },
  {
    id: 'PROD-MARKETING-01',
    type: 'Production Printer',
    category: 'production',
    model: 'HP Color LaserJet Enterprise M856',
    location: 'Marketing Print Center',
    floor: '4th',
    status: 'protected', // AI intervened
    ipAddress: '192.168.10.302',
    serialNumber: 'CNBKM32346',
    revenuePerDay: 3800,
    contractValue: 165000,
    metrics: {
      tonerBlack: 48,
      tonerCyan: 42,
      tonerMagenta: 45,
      tonerYellow: 39,
      paperLevel: 65,
      pagesThisMonth: 22150,
      avgPagesPerDay: 738,
      temperature: 43,
      uptime: 98.7,
      networkLatency: 45, // elevated but managed
    },
    slaWindow: { start: '08:00', end: '18:00' },
    businessCriticality: 'high',
    aiIntervention: {
      type: 'PRIORITY_BOOST',
      reason: 'High-value print job detected, traffic prioritized',
      timestamp: new Date(Date.now() - 600000).toISOString(),
      revenueProtected: 4500,
    },
    connectivity: 'Online',
  },
  // Offline printer
  {
    id: 'PR-WAREHOUSE-01',
    type: 'Workgroup Printer',
    category: 'workgroup',
    model: 'HP LaserJet Pro M404dn',
    location: 'Warehouse Office',
    floor: 'Warehouse',
    status: 'offline',
    ipAddress: '192.168.10.250',
    serialNumber: 'CNBKM22399',
    revenuePerDay: 450,
    contractValue: 18000,
    metrics: {
      tonerBlack: 45,
      paperLevel: 60,
      pagesThisMonth: 1250,
      avgPagesPerDay: 42,
      uptime: 78.5,
    },
    slaWindow: { start: '07:00', end: '17:00' },
    businessCriticality: 'low',
    connectivity: 'Offline',
    lastSeen: '2025-01-18T10:45:00Z',
  },
];

// ════════════════════════════════════════════════════════════════════════════
// REVENUE STATUS TYPES - Business Impact Focused
// ════════════════════════════════════════════════════════════════════════════

const REVENUE_STATUS = {
  SAFE: { 
    icon: '🟢', 
    text: 'REVENUE SAFE', 
    shortText: 'SAFE',
    bg: 'bg-emerald-500', 
    glow: 'from-emerald-500/30 to-teal-500/30', 
    color: 'text-emerald-400',
    borderColor: 'border-emerald-400/60',
    description: 'Service operating within SLA'
  },
  AT_RISK: { 
    icon: '🟡', 
    text: 'REVENUE AT RISK', 
    shortText: 'AT RISK',
    bg: 'bg-amber-500', 
    glow: 'from-amber-500/50 to-yellow-500/50', 
    color: 'text-amber-400',
    borderColor: 'border-amber-400/60',
    description: 'Service degradation predicted'
  },
  CRITICAL: { 
    icon: '🔴', 
    text: 'REVENUE IMPACT', 
    shortText: 'IMPACT',
    bg: 'bg-red-500', 
    glow: 'from-red-500/50 to-rose-500/50', 
    color: 'text-red-400',
    borderColor: 'border-red-400/60',
    description: 'Immediate revenue impact'
  },
  PROTECTED: { 
    icon: '🔵', 
    text: 'REVENUE PROTECTED', 
    shortText: 'PROTECTED',
    bg: 'bg-blue-500', 
    glow: 'from-blue-500/30 to-cyan-500/30', 
    color: 'text-blue-400',
    borderColor: 'border-blue-400/60',
    description: 'AI intervention active'
  },
  OFFLINE: { 
    icon: '⚫', 
    text: 'SERVICE DOWN', 
    shortText: 'DOWN',
    bg: 'bg-slate-600', 
    glow: 'from-slate-500/30 to-gray-500/30', 
    color: 'text-slate-400',
    borderColor: 'border-slate-500/60',
    description: 'Device unreachable'
  },
};

const CATEGORY_STYLES = {
  mfp: { icon: '🖨️', color: 'text-blue-300', bg: 'from-blue-400/30 to-cyan-500/30', border: 'border-blue-400/60', label: 'Enterprise MFP' },
  workgroup: { icon: '📠', color: 'text-emerald-300', bg: 'from-emerald-400/30 to-teal-500/30', border: 'border-emerald-400/60', label: 'Workgroup' },
  production: { icon: '🏭', color: 'text-purple-300', bg: 'from-purple-400/30 to-violet-500/30', border: 'border-purple-400/60', label: 'Production' },
};

// ════════════════════════════════════════════════════════════════════════════
// REVENUE IMPACT ANALYSIS ENGINE
// ════════════════════════════════════════════════════════════════════════════

const analyzeRevenueImpact = (printers) => {
  const impacts = [];
  const now = Date.now();
  
  printers.forEach(printer => {
    const m = printer.metrics;
    const dailyRevenue = printer.revenuePerDay || 0;
    
    // Skip offline check first
    if (printer.connectivity === 'Offline' || printer.status === 'offline') {
      const lastSeen = printer.lastSeen ? new Date(printer.lastSeen).getTime() : null;
      const hoursOffline = lastSeen ? Math.floor((now - lastSeen) / 3600000) : 8;
      const revenueImpact = Math.round((dailyRevenue / 24) * hoursOffline);
      
      impacts.push({
        id: `offline-${printer.id}`,
        type: 'SERVICE_DOWN',
        printerId: printer.id,
        severity: 'critical',
        revenueImpact: revenueImpact,
        hoursToImpact: 0,
        description: `Service unavailable - $${revenueImpact.toLocaleString()} impact`,
        cause: `Device offline for ${hoursOffline} hours`,
        recommendation: 'Dispatch technician. Check network connectivity and power.',
        confidence: 98,
        action: { type: 'DISPATCH_TECH', label: 'Create Service Ticket' },
      });
      return;
    }
    
    // Check for AI-protected devices
    if (printer.aiIntervention) {
      impacts.push({
        id: `protected-${printer.id}`,
        type: 'REVENUE_PROTECTED',
        printerId: printer.id,
        severity: 'protected',
        revenueProtected: printer.aiIntervention.revenueProtected,
        hoursToImpact: null,
        description: `Revenue protected via edge intervention`,
        cause: printer.aiIntervention.reason,
        recommendation: 'AI action successful. Monitor for stability.',
        confidence: 94,
        intervention: printer.aiIntervention,
      });
    }
    
    // Toner critical - revenue impact prediction
    const tonerLevels = [
      { name: 'Black', level: m.tonerBlack },
      { name: 'Cyan', level: m.tonerCyan },
      { name: 'Magenta', level: m.tonerMagenta },
      { name: 'Yellow', level: m.tonerYellow },
    ].filter(t => t.level !== null && t.level !== undefined);
    
    tonerLevels.forEach(toner => {
      if (toner.level <= 10) {
        const pagesRemaining = toner.level * 50;
        const hoursToImpact = Math.max(1, Math.floor(pagesRemaining / (m.avgPagesPerDay / 8)));
        const revenueAtRisk = Math.round(dailyRevenue * (hoursToImpact / 24));
        
        impacts.push({
          id: `toner-${printer.id}-${toner.name.toLowerCase()}`,
          type: 'SERVICE_DEGRADATION_PREDICTED',
          printerId: printer.id,
          severity: toner.level <= 5 ? 'critical' : 'warning',
          revenueImpact: revenueAtRisk,
          hoursToImpact: hoursToImpact,
          description: `Revenue impact predicted in ~${hoursToImpact} hours`,
          cause: `${toner.name} toner at ${toner.level}% (~${pagesRemaining} pages remaining)`,
          recommendation: `Order replacement ${toner.name.toLowerCase()} toner via HP Smart Services.`,
          confidence: 97,
          action: { type: 'AUTO_ORDER', label: 'Order Supplies' },
        });
      } else if (toner.level <= 20) {
        const pagesRemaining = toner.level * 50;
        const hoursToImpact = Math.floor(pagesRemaining / (m.avgPagesPerDay / 8));
        const revenueAtRisk = Math.round(dailyRevenue * 0.25);
        
        impacts.push({
          id: `toner-low-${printer.id}-${toner.name.toLowerCase()}`,
          type: 'SLA_RISK',
          printerId: printer.id,
          severity: 'warning',
          revenueImpact: revenueAtRisk,
          hoursToImpact: hoursToImpact,
          description: `SLA risk in ${hoursToImpact} hours without action`,
          cause: `${toner.name} toner at ${toner.level}%`,
          recommendation: `Schedule toner replacement before next SLA window.`,
          confidence: 92,
          action: { type: 'SCHEDULE_ORDER', label: 'Add to Order Queue' },
        });
      }
    });
    
    // Network degradation
    if (m.networkLatency && m.networkLatency > 30) {
      const revenueAtRisk = Math.round(dailyRevenue * 0.15);
      impacts.push({
        id: `network-${printer.id}`,
        type: 'SERVICE_DEGRADATION_PREDICTED',
        printerId: printer.id,
        severity: m.networkLatency > 50 ? 'critical' : 'warning',
        revenueImpact: revenueAtRisk,
        hoursToImpact: 2,
        description: `Service degradation affecting print throughput`,
        cause: `Network latency elevated (${m.networkLatency}ms)`,
        recommendation: 'Re-route HP traffic to secondary path.',
        confidence: 89,
        action: { type: 'REROUTE_TRAFFIC', label: 'Approve Edge Action' },
      });
    }
    
    // High temperature - mechanical risk
    if (m.temperature && m.temperature > 55) {
      const revenueAtRisk = Math.round(dailyRevenue * 0.5);
      impacts.push({
        id: `temp-${printer.id}`,
        type: 'HARDWARE_FAILURE_RISK',
        printerId: printer.id,
        severity: m.temperature > 60 ? 'critical' : 'warning',
        revenueImpact: revenueAtRisk,
        hoursToImpact: 4,
        description: `Hardware failure risk - thermal threshold exceeded`,
        cause: `Device temperature at ${m.temperature}°C`,
        recommendation: 'Redistribute print load. Check ventilation.',
        confidence: 86,
        action: { type: 'LOAD_BALANCE', label: 'Balance Load' },
      });
    }
    
    // High jam rate
    if (m.jamRate && m.jamRate > 2) {
      const revenueAtRisk = Math.round(dailyRevenue * 0.2);
      impacts.push({
        id: `jam-${printer.id}`,
        type: 'SERVICE_DEGRADATION',
        printerId: printer.id,
        severity: 'warning',
        revenueImpact: revenueAtRisk,
        hoursToImpact: 8,
        description: `Print throughput reduced - elevated failure rate`,
        cause: `Paper jam rate at ${m.jamRate}%`,
        recommendation: 'Schedule preventive maintenance.',
        confidence: 84,
        action: { type: 'SCHEDULE_SERVICE', label: 'Schedule Service' },
      });
    }
  });
  
  return impacts.sort((a, b) => {
    const sevOrder = { critical: 0, warning: 1, protected: 2, info: 3 };
    return (sevOrder[a.severity] || 3) - (sevOrder[b.severity] || 3);
  });
};

const getPrinterRevenueStatus = (printer, impacts) => {
  if (printer.connectivity === 'Offline' || printer.status === 'offline') {
    return REVENUE_STATUS.OFFLINE;
  }
  
  if (printer.aiIntervention) {
    return REVENUE_STATUS.PROTECTED;
  }
  
  const printerImpacts = impacts.filter(i => i.printerId === printer.id);
  const hasCritical = printerImpacts.some(i => i.severity === 'critical');
  const hasWarning = printerImpacts.some(i => i.severity === 'warning');
  
  if (hasCritical) return REVENUE_STATUS.CRITICAL;
  if (hasWarning) return REVENUE_STATUS.AT_RISK;
  return REVENUE_STATUS.SAFE;
};

// ════════════════════════════════════════════════════════════════════════════
// CUSTOM NODE COMPONENTS
// ════════════════════════════════════════════════════════════════════════════

// Print Operations Center Node (Cloud)
const PrintOpsNode = ({ data }) => (
  <div className="relative group">
    <Handle type="source" position={Position.Bottom} className="!w-3 !h-3 !bg-cyan-400 !border-2 !border-white" />
    
    <div className="absolute -inset-2 bg-gradient-to-r from-cyan-400/30 via-blue-400/30 to-cyan-400/30 rounded-2xl blur-xl opacity-75 group-hover:opacity-100 transition-opacity duration-500" />
    
    <div className="relative bg-gradient-to-br from-slate-700 via-slate-800 to-slate-900 border-2 border-cyan-400/50 rounded-2xl p-6 min-w-[280px] shadow-2xl shadow-cyan-500/20">
      <div className="absolute -top-1.5 -right-1.5 w-4 h-4">
        <span className="absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75 animate-ping" />
        <span className="relative inline-flex rounded-full h-4 w-4 bg-emerald-400 shadow-lg shadow-emerald-400/50" />
      </div>
      
      <div className="flex items-center justify-center mb-3">
        <div className="w-14 h-14 bg-gradient-to-br from-blue-400/30 to-cyan-500/30 rounded-xl flex items-center justify-center border border-cyan-400/50">
          <span className="text-3xl">💼</span>
        </div>
      </div>
      
      <div className="text-center">
        <div className="text-sm font-semibold text-white tracking-wide">{data.label}</div>
        <div className="text-xs text-slate-300 mt-1">{data.sublabel}</div>
      </div>
      
      <div className="mt-4 pt-3 border-t border-slate-600/50 grid grid-cols-2 gap-3">
        <div className="text-center">
          <div className="text-lg font-bold text-emerald-300">${Math.floor(data.revenueProtected/1000)}K</div>
          <div className="text-[10px] text-slate-400 uppercase tracking-wider">Protected</div>
        </div>
        <div className="text-center">
          <div className="text-lg font-bold text-blue-300">{data.printerCount}</div>
          <div className="text-[10px] text-slate-400 uppercase tracking-wider">Devices</div>
        </div>
      </div>
      
      <div className="mt-3 flex items-center justify-center gap-1.5">
        <div className="w-2 h-2 rounded-full bg-cyan-400 animate-pulse" />
        <span className="text-[10px] text-cyan-300 font-medium">HP Edge Control Active</span>
      </div>
    </div>
  </div>
);

// Site Gateway Node
const SiteGatewayNode = ({ data }) => {
  const isOnline = data.status === 'Online';
  const hasRisk = data.revenueAtRisk > 0;
  
  return (
    <div className="relative group cursor-pointer">
      <Handle type="target" position={Position.Top} className="!w-3 !h-3 !bg-emerald-400 !border-2 !border-white" />
      <Handle type="source" position={Position.Bottom} className="!w-3 !h-3 !bg-blue-400 !border-2 !border-white" />
      
      <div className={`absolute -inset-1.5 bg-gradient-to-r ${isOnline ? 'from-emerald-400/30 to-teal-400/30' : 'from-red-400/30 to-orange-400/30'} rounded-xl blur-lg opacity-0 group-hover:opacity-100 transition-all duration-300`} />
      
      <div className={`relative bg-gradient-to-br from-slate-600 to-slate-700 border-2 ${isOnline ? 'border-emerald-400/60' : 'border-red-400/60'} rounded-xl p-4 min-w-[280px] shadow-xl transition-all duration-300 group-hover:scale-105`}>
        <div className="absolute -top-1 -right-1 w-3 h-3">
          {isOnline && <span className="absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75 animate-ping" />}
          <span className={`relative inline-flex rounded-full h-3 w-3 ${isOnline ? 'bg-emerald-400' : 'bg-red-400'} shadow-lg`} />
        </div>
        
        <div className="flex items-center gap-3">
          <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${isOnline ? 'bg-emerald-500/30' : 'bg-red-500/30'}`}>
            <svg className={`w-5 h-5 ${isOnline ? 'text-emerald-300' : 'text-red-300'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M5 12h14M5 12a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v4a2 2 0 01-2 2M5 12a2 2 0 00-2 2v4a2 2 0 002 2h14a2 2 0 002-2v-4a2 2 0 00-2-2" />
            </svg>
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-sm font-semibold text-white truncate">{data.name}</div>
            <div className="text-[10px] text-slate-400 truncate">{data.location}</div>
          </div>
        </div>
        
        <div className="mt-3 pt-2 border-t border-slate-500/30 grid grid-cols-3 gap-2 text-xs">
          <div>
            <span className="text-slate-400">SLA</span>
            <div className="font-semibold text-emerald-300">{data.slaCompliance}%</div>
          </div>
          <div className="text-center">
            <span className="text-slate-400">Devices</span>
            <div className="font-semibold text-blue-300">{data.printerCount}</div>
          </div>
          <div className="text-right">
            <span className="text-slate-400">At Risk</span>
            <div className={`font-semibold ${hasRisk ? 'text-amber-300' : 'text-slate-300'}`}>
              {hasRisk ? `$${Math.floor(data.revenueAtRisk/1000)}K` : '$0'}
            </div>
          </div>
        </div>
        
        <div className="mt-2 text-center">
          <span className="text-[9px] px-2 py-0.5 bg-slate-800/50 rounded-full text-cyan-300 font-mono">
            {data.stratusModel}
          </span>
        </div>
      </div>
    </div>
  );
};

// Printer Node with Revenue Status Badge
const PrinterNode = ({ data }) => {
  const categoryStyle = CATEGORY_STYLES[data.category] || CATEGORY_STYLES.workgroup;
  const revenueStatus = data.revenueStatus || REVENUE_STATUS.SAFE;
  const m = data.metrics || {};
  
  const isAtRisk = revenueStatus === REVENUE_STATUS.AT_RISK || revenueStatus === REVENUE_STATUS.CRITICAL;
  const isProtected = revenueStatus === REVENUE_STATUS.PROTECTED;
  
  return (
    <div className="relative group cursor-pointer">
      <Handle type="target" position={Position.Top} className="!w-3 !h-3 !bg-blue-400 !border-2 !border-white" />
      
      {/* Glow effect based on revenue status */}
      {(isAtRisk || isProtected) && (
        <div className={`absolute -inset-2 bg-gradient-to-r ${revenueStatus.glow} rounded-xl blur-lg opacity-60 ${isAtRisk ? 'animate-pulse' : ''}`} />
      )}
      
      <div className={`relative bg-gradient-to-br from-slate-600 to-slate-700 border-2 ${revenueStatus.borderColor} rounded-lg p-3 min-w-[175px] shadow-lg transition-all duration-300 group-hover:scale-105`}>
        
        {/* Revenue Status Badge - THE KEY SEMANTIC OVERLAY */}
        <div className={`absolute -top-2 -right-2 px-2 py-0.5 ${revenueStatus.bg} text-white text-[9px] font-bold rounded-full shadow-lg flex items-center gap-1`}>
          <span>{revenueStatus.icon}</span>
          <span>{revenueStatus.shortText}</span>
        </div>
        
        {/* Printer header */}
        <div className="flex items-center gap-2 mb-2">
          <div className={`w-9 h-9 rounded-lg flex items-center justify-center bg-gradient-to-br ${categoryStyle.bg}`}>
            <span className="text-xl">{categoryStyle.icon}</span>
          </div>
          <div>
            <div className="text-xs font-bold text-white">{data.id}</div>
            <div className="text-[10px] text-slate-400">{data.location}</div>
          </div>
        </div>
        
        {/* Revenue metrics */}
        <div className="grid grid-cols-2 gap-1 text-center mb-2">
          <div className="rounded px-1 py-0.5 bg-slate-700/50">
            <div className="text-[10px] font-bold text-emerald-300">${data.revenuePerDay?.toLocaleString() || 0}</div>
            <div className="text-[8px] text-slate-500">DAILY REV</div>
          </div>
          <div className={`rounded px-1 py-0.5 ${m.networkLatency > 30 ? 'bg-amber-500/20' : 'bg-slate-700/50'}`}>
            <div className={`text-[10px] font-bold ${m.networkLatency > 30 ? 'text-amber-300' : 'text-slate-300'}`}>
              {m.networkLatency || 0}ms
            </div>
            <div className="text-[8px] text-slate-500">LATENCY</div>
          </div>
        </div>
        
        {/* Connectivity status */}
        <div className="text-center">
          <span className={`text-[10px] px-2 py-0.5 rounded-full ${
            data.connectivity === 'Online' ? 'bg-emerald-500/30 text-emerald-300 border border-emerald-400/30' :
            'bg-red-500/30 text-red-300 border border-red-400/30'
          }`}>
            {data.connectivity}
          </span>
        </div>
        
        {/* AI Intervention indicator */}
        {data.aiIntervention && (
          <div className="mt-2 pt-2 border-t border-slate-600/50 text-center">
            <span className="text-[9px] text-blue-300">🔵 AI Protected</span>
          </div>
        )}
      </div>
    </div>
  );
};

// Node types registry
const nodeTypes = {
  printOps: PrintOpsNode,
  siteGateway: SiteGatewayNode,
  printer: PrinterNode,
};

// ════════════════════════════════════════════════════════════════════════════
// REVENUE IMPACT PANEL (Right-Side Panel)
// ════════════════════════════════════════════════════════════════════════════

const RevenueImpactPanel = ({ isOpen, onClose, impacts, printers, gateway }) => {
  const [activeTab, setActiveTab] = useState('impacts');
  const [executingAction, setExecutingAction] = useState(null);
  
  if (!isOpen) return null;
  
  const handleApproveAction = async (impact) => {
    setExecutingAction(impact.id);
    await new Promise(resolve => setTimeout(resolve, 1500));
    setExecutingAction(null);
  };
  
  const criticalCount = impacts.filter(i => i.severity === 'critical').length;
  const protectedCount = impacts.filter(i => i.type === 'REVENUE_PROTECTED').length;
  const totalAtRisk = impacts
    .filter(i => i.revenueImpact && i.severity !== 'protected')
    .reduce((sum, i) => sum + i.revenueImpact, 0);
  const totalProtected = impacts
    .filter(i => i.revenueProtected)
    .reduce((sum, i) => sum + i.revenueProtected, 0);
  
  return (
    <div className="absolute top-0 right-0 h-full w-[420px] bg-slate-900/98 backdrop-blur-xl border-l border-slate-700/50 shadow-2xl z-50 flex flex-col animate-slide-in-right">
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-500/20 to-cyan-500/20 px-4 py-3 border-b border-slate-700/50">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 bg-blue-500/20 rounded-lg flex items-center justify-center">
              <span className="text-lg">💼</span>
            </div>
            <div>
              <h3 className="text-sm font-bold text-white">Revenue Control</h3>
              <div className="text-[10px] text-slate-400">Edge Control Plane • AI Active</div>
            </div>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-white p-1">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
        
        {/* Quick Stats */}
        <div className="grid grid-cols-2 gap-2 mt-3">
          <div className="bg-amber-500/20 rounded-lg px-3 py-2 border border-amber-500/30">
            <div className="text-lg font-bold text-amber-300">${totalAtRisk.toLocaleString()}</div>
            <div className="text-[10px] text-amber-300/70">Revenue At Risk</div>
          </div>
          <div className="bg-blue-500/20 rounded-lg px-3 py-2 border border-blue-500/30">
            <div className="text-lg font-bold text-blue-300">${totalProtected.toLocaleString()}</div>
            <div className="text-[10px] text-blue-300/70">Revenue Protected</div>
          </div>
        </div>
      </div>
      
      {/* Tabs */}
      <div className="flex border-b border-slate-700/50">
        <button
          onClick={() => setActiveTab('impacts')}
          className={`flex-1 px-4 py-2 text-xs font-medium transition-colors ${
            activeTab === 'impacts'
              ? 'text-cyan-300 border-b-2 border-cyan-400 bg-cyan-500/10'
              : 'text-slate-400 hover:text-white'
          }`}
        >
          <span className="flex items-center justify-center gap-1">
            ⚠️ Impact Analysis
            {criticalCount > 0 && (
              <span className="px-1.5 py-0.5 bg-red-500 text-white text-[10px] rounded-full">{criticalCount}</span>
            )}
          </span>
        </button>
        <button
          onClick={() => setActiveTab('protected')}
          className={`flex-1 px-4 py-2 text-xs font-medium transition-colors ${
            activeTab === 'protected'
              ? 'text-cyan-300 border-b-2 border-cyan-400 bg-cyan-500/10'
              : 'text-slate-400 hover:text-white'
          }`}
        >
          <span className="flex items-center justify-center gap-1">
            🔵 Protected
            {protectedCount > 0 && (
              <span className="px-1.5 py-0.5 bg-blue-500 text-white text-[10px] rounded-full">{protectedCount}</span>
            )}
          </span>
        </button>
      </div>
      
      {/* Content */}
      <div className="flex-1 overflow-y-auto p-4 space-y-3">
        {activeTab === 'impacts' && (
          <>
            {impacts.filter(i => i.severity !== 'protected').length === 0 ? (
              <div className="text-center text-slate-500 py-8">
                <div className="text-3xl mb-2">✓</div>
                <div className="text-emerald-400 font-medium">All Revenue Secure</div>
                <div className="text-xs text-slate-500 mt-1">No predicted service impacts</div>
              </div>
            ) : (
              impacts.filter(i => i.severity !== 'protected').map(impact => {
                const printer = printers.find(p => p.id === impact.printerId);
                const categoryStyle = CATEGORY_STYLES[printer?.category] || CATEGORY_STYLES.workgroup;
                
                return (
                  <div
                    key={impact.id}
                    className={`bg-slate-800/50 rounded-lg border ${
                      impact.severity === 'critical' ? 'border-red-500/50' : 'border-amber-500/30'
                    } p-4`}
                  >
                    {/* LEAD WITH REVENUE IMPACT */}
                    <div className="flex items-center justify-between mb-3">
                      <div className={`text-lg font-bold ${impact.severity === 'critical' ? 'text-red-400' : 'text-amber-400'}`}>
                        ${impact.revenueImpact?.toLocaleString() || 0} at risk
                      </div>
                      <div className="text-right">
                        <div className="text-xs font-bold text-cyan-400">{impact.confidence}%</div>
                        <div className="text-[10px] text-slate-500">confidence</div>
                      </div>
                    </div>
                    
                    {/* Time to impact */}
                    {impact.hoursToImpact !== null && impact.hoursToImpact !== undefined && (
                      <div className={`text-sm mb-2 ${impact.severity === 'critical' ? 'text-red-300' : 'text-amber-300'}`}>
                        {impact.hoursToImpact === 0 
                          ? '⚡ Immediate impact' 
                          : `⏱️ Impact in ~${impact.hoursToImpact} hours`}
                      </div>
                    )}
                    
                    {/* Device info */}
                    <div className="flex items-center gap-2 mb-3 bg-slate-700/30 rounded p-2">
                      <span className="text-lg">{categoryStyle.icon}</span>
                      <div>
                        <div className="text-xs font-semibold text-white">{impact.printerId}</div>
                        <div className="text-[10px] text-slate-400">{printer?.location}</div>
                      </div>
                    </div>
                    
                    {/* Cause - Secondary to revenue */}
                    <div className="bg-slate-700/30 rounded p-3 mb-3">
                      <div className="text-[10px] text-slate-500 uppercase mb-1">Root Cause</div>
                      <div className="text-xs text-slate-300">{impact.cause}</div>
                    </div>
                    
                    {/* Recommendation */}
                    <div className="bg-cyan-500/10 border border-cyan-500/30 rounded p-3 mb-3">
                      <div className="text-[10px] text-cyan-400 uppercase mb-1">Recommended Action</div>
                      <div className="text-xs text-cyan-300">{impact.recommendation}</div>
                    </div>
                    
                    {/* Action buttons */}
                    {impact.action && (
                      <div className="flex gap-2">
                        <button
                          onClick={() => handleApproveAction(impact)}
                          disabled={executingAction === impact.id}
                          className="flex-1 px-3 py-2 bg-cyan-500 hover:bg-cyan-400 disabled:bg-slate-600 text-white text-xs font-medium rounded-lg transition-colors flex items-center justify-center gap-1"
                        >
                          {executingAction === impact.id ? (
                            <>
                              <div className="w-3 h-3 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                              Processing...
                            </>
                          ) : (
                            <>✓ {impact.action.label}</>
                          )}
                        </button>
                        <button className="px-3 py-2 bg-slate-700 hover:bg-slate-600 text-slate-300 text-xs rounded-lg transition-colors">
                          Dismiss
                        </button>
                      </div>
                    )}
                  </div>
                );
              })
            )}
          </>
        )}
        
        {activeTab === 'protected' && (
          <div className="space-y-3">
            {impacts.filter(i => i.type === 'REVENUE_PROTECTED').length === 0 ? (
              <div className="text-center text-slate-500 py-8">
                <div className="text-3xl mb-2">🔵</div>
                <div>No active AI interventions</div>
              </div>
            ) : (
              impacts.filter(i => i.type === 'REVENUE_PROTECTED').map(impact => {
                const printer = printers.find(p => p.id === impact.printerId);
                const categoryStyle = CATEGORY_STYLES[printer?.category] || CATEGORY_STYLES.workgroup;
                
                return (
                  <div key={impact.id} className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-4">
                    <div className="flex items-center justify-between mb-3">
                      <div className="text-lg font-bold text-blue-300">
                        ${impact.revenueProtected?.toLocaleString() || 0} protected
                      </div>
                      <span className="px-2 py-1 bg-blue-500 text-white text-[10px] rounded-full">AI Active</span>
                    </div>
                    
                    <div className="flex items-center gap-2 mb-3 bg-slate-700/30 rounded p-2">
                      <span className="text-lg">{categoryStyle.icon}</span>
                      <div>
                        <div className="text-xs font-semibold text-white">{impact.printerId}</div>
                        <div className="text-[10px] text-slate-400">{printer?.location}</div>
                      </div>
                    </div>
                    
                    <div className="text-xs text-slate-300 mb-2">
                      <span className="text-blue-400 font-medium">Intervention:</span> {impact.intervention?.type.replace(/_/g, ' ')}
                    </div>
                    <div className="text-xs text-slate-400">{impact.cause}</div>
                  </div>
                );
              })
            )}
          </div>
        )}
      </div>
      
      {/* Footer */}
      <div className="border-t border-slate-700/50 px-4 py-2 bg-slate-800/50">
        <div className="flex items-center justify-between text-[10px] text-slate-500">
          <span>Celerway Edge Control Plane</span>
          <span className="text-cyan-400">HP Smart Device Services</span>
        </div>
      </div>
    </div>
  );
};

// ════════════════════════════════════════════════════════════════════════════
// PROACTIVE ALERT - Revenue Framed
// ════════════════════════════════════════════════════════════════════════════

const ProactiveRevenueAlert = ({ impacts, printers, onViewDetails }) => {
  const criticalImpacts = impacts.filter(i => i.severity === 'critical');
  if (criticalImpacts.length === 0) return null;
  
  const topImpact = criticalImpacts[0];
  const totalAtRisk = criticalImpacts.reduce((sum, i) => sum + (i.revenueImpact || 0), 0);
  
  return (
    <div className="absolute top-32 left-6 z-40 animate-slide-in-left">
      <div className="bg-gradient-to-r from-red-900/90 to-slate-900/90 backdrop-blur-xl border border-red-500/50 rounded-xl p-4 shadow-2xl shadow-red-500/20 max-w-sm">
        <div className="flex items-start gap-3">
          <div className="w-12 h-12 bg-red-500/30 rounded-full flex items-center justify-center flex-shrink-0">
            <span className="text-2xl">💰</span>
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-lg font-bold text-white mb-1">
              ${totalAtRisk.toLocaleString()} at risk
            </div>
            <div className="text-xs text-red-200 mb-2">
              {topImpact.description}
            </div>
            <div className="text-[10px] text-slate-400 mb-3">
              {criticalImpacts.length} service{criticalImpacts.length > 1 ? 's' : ''} requiring attention
            </div>
            <button 
              onClick={onViewDetails}
              className="px-4 py-1.5 bg-red-500 hover:bg-red-400 text-white text-xs font-medium rounded-lg transition-colors"
            >
              View Details
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// ════════════════════════════════════════════════════════════════════════════
// STATS BAR - Revenue Focused
// ════════════════════════════════════════════════════════════════════════════

const RevenueStatsBar = ({ printers, impacts, gateway, onOpenPanel }) => {
  const online = printers.filter(p => p.connectivity === 'Online').length;
  const criticalCount = impacts.filter(i => i.severity === 'critical').length;
  const protectedCount = impacts.filter(i => i.type === 'REVENUE_PROTECTED').length;
  const totalProtected = impacts
    .filter(i => i.revenueProtected)
    .reduce((sum, i) => sum + i.revenueProtected, 0);
  
  return (
    <div className="flex items-center gap-3">
      <div className="flex items-center gap-2 bg-emerald-500/20 rounded-lg px-3 py-2 border border-emerald-500/30">
        <span className="text-emerald-300 font-bold text-lg">${Math.floor(gateway.revenueProtected/1000)}K</span>
        <span className="text-emerald-300/70 text-xs">Protected</span>
      </div>
      <div className="flex items-center gap-2 bg-slate-700/50 rounded-lg px-3 py-2 border border-slate-600/30">
        <span className="text-blue-300 font-bold text-lg">{online}</span>
        <span className="text-slate-300 text-xs">/ {printers.length} Online</span>
      </div>
      {criticalCount > 0 && (
        <div className="flex items-center gap-2 bg-red-500/20 rounded-lg px-3 py-2 border border-red-400/50 animate-pulse">
          <span className="text-red-300 font-bold text-lg">{criticalCount}</span>
          <span className="text-red-300/70 text-xs">At Risk</span>
        </div>
      )}
      {protectedCount > 0 && (
        <div className="flex items-center gap-2 bg-blue-500/20 rounded-lg px-3 py-2 border border-blue-400/50">
          <span className="text-blue-300 font-bold text-lg">{protectedCount}</span>
          <span className="text-blue-300/70 text-xs">AI Active</span>
        </div>
      )}
      <button
        onClick={onOpenPanel}
        className="flex items-center gap-2 bg-cyan-500/20 hover:bg-cyan-500/30 rounded-lg px-4 py-2 border border-cyan-400/50 transition-colors"
      >
        <span className="text-cyan-300 text-sm">💼</span>
        <span className="text-cyan-300 text-xs font-medium">Control Panel</span>
        {impacts.length > 0 && (
          <span className="px-1.5 py-0.5 bg-cyan-500 text-white text-[10px] rounded-full">{impacts.length}</span>
        )}
      </button>
    </div>
  );
};

// ════════════════════════════════════════════════════════════════════════════
// LEGEND
// ════════════════════════════════════════════════════════════════════════════

const Legend = () => (
  <div className="bg-slate-800/90 backdrop-blur-sm border border-slate-600/50 rounded-xl p-4 space-y-3">
    <div className="text-xs font-semibold text-slate-200 uppercase tracking-wider">Revenue Status</div>
    <div className="space-y-2">
      {Object.entries(REVENUE_STATUS).map(([key, status]) => (
        <div key={key} className="flex items-center gap-2">
          <span className="text-sm">{status.icon}</span>
          <span className="text-xs text-slate-300">{status.text}</span>
        </div>
      ))}
    </div>
  </div>
);

// ════════════════════════════════════════════════════════════════════════════
// MAIN COMPONENT
// ════════════════════════════════════════════════════════════════════════════

const HPRevenueControlSentry = () => {
  const [nodes, setNodes, onNodesChange] = useNodesState([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState([]);
  const [printers] = useState(MOCK_PRINTERS);
  const [gateway] = useState(MOCK_SITE_GATEWAY);
  const [impacts, setImpacts] = useState([]);
  const [isPanelOpen, setIsPanelOpen] = useState(false);
  const [loading, setLoading] = useState(true);

  // Build topology
  const buildTopology = useCallback(() => {
    const analyzedImpacts = analyzeRevenueImpact(printers);
    setImpacts(analyzedImpacts);
    
    const newNodes = [];
    const newEdges = [];
    
    // Layout constants
    const CLOUD_Y = 0;
    const GATEWAY_Y = 180;
    const PRINTER_Y_START = 400;
    const CENTER_X = 600;
    const PRINTER_GAP = 195;
    const CATEGORY_GAP = 180;
    
    // Group printers by category
    const categories = ['mfp', 'workgroup', 'production'];
    const printersByCategory = {};
    categories.forEach(cat => {
      printersByCategory[cat] = printers.filter(p => p.category === cat);
    });
    
    // Calculate totals
    const totalRevenue = printers.reduce((sum, p) => sum + (p.revenuePerDay || 0), 0);
    const protectedRevenue = analyzedImpacts
      .filter(i => i.revenueProtected)
      .reduce((sum, i) => sum + i.revenueProtected, 0) + (gateway.revenueProtected || 0);
    
    // 1. Revenue Control Center (Cloud)
    newNodes.push({
      id: 'revenue-control',
      type: 'printOps',
      position: { x: CENTER_X, y: CLOUD_Y },
      data: {
        label: 'HP Revenue Control',
        sublabel: 'Edge Control Plane',
        printerCount: printers.length,
        revenueProtected: protectedRevenue,
        dailyRevenue: totalRevenue,
      },
    });
    
    // 2. Site Gateway
    const atRiskRevenue = analyzedImpacts
      .filter(i => i.revenueImpact && i.severity !== 'protected')
      .reduce((sum, i) => sum + i.revenueImpact, 0);
    
    newNodes.push({
      id: `gateway-${gateway.id}`,
      type: 'siteGateway',
      position: { x: CENTER_X, y: GATEWAY_Y },
      data: { ...gateway, revenueAtRisk: atRiskRevenue },
    });
    
    // Edge: Cloud → Gateway
    newEdges.push({
      id: 'cloud-to-gateway',
      source: 'revenue-control',
      target: `gateway-${gateway.id}`,
      type: 'smoothstep',
      animated: true,
      style: { stroke: '#22d3ee', strokeWidth: 2 },
    });
    
    // 3. Printers by category in rows
    let currentY = PRINTER_Y_START;
    
    categories.forEach(category => {
      const categoryPrinters = printersByCategory[category];
      if (categoryPrinters.length === 0) return;
      
      const startX = CENTER_X - ((categoryPrinters.length - 1) * PRINTER_GAP) / 2;
      
      categoryPrinters.forEach((printer, idx) => {
        const revenueStatus = getPrinterRevenueStatus(printer, analyzedImpacts);
        const printerId = `printer-${printer.id}`;
        
        newNodes.push({
          id: printerId,
          type: 'printer',
          position: { x: startX + idx * PRINTER_GAP, y: currentY },
          data: { ...printer, revenueStatus },
        });
        
        // Edge: Gateway → Printer
        const edgeColor = 
          revenueStatus === REVENUE_STATUS.CRITICAL ? '#ef4444' :
          revenueStatus === REVENUE_STATUS.AT_RISK ? '#f59e0b' :
          revenueStatus === REVENUE_STATUS.PROTECTED ? '#3b82f6' :
          revenueStatus === REVENUE_STATUS.OFFLINE ? '#64748b' : '#22d3ee';
        
        newEdges.push({
          id: `gateway-to-${printerId}`,
          source: `gateway-${gateway.id}`,
          target: printerId,
          type: 'smoothstep',
          animated: printer.connectivity !== 'Offline',
          style: { stroke: edgeColor, strokeWidth: 2 },
        });
      });
      
      currentY += CATEGORY_GAP;
    });
    
    return { nodes: newNodes, edges: newEdges };
  }, [printers, gateway]);

  // Initialize
  useEffect(() => {
    setLoading(true);
    
    setTimeout(() => {
      const { nodes: newNodes, edges: newEdges } = buildTopology();
      setNodes(newNodes);
      setEdges(newEdges);
      setLoading(false);
    }, 1000);
  }, [buildTopology, setNodes, setEdges]);

  return (
    <div className="h-[calc(100vh-64px)] w-full bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 relative overflow-hidden">
      {/* Header */}
      <div className="absolute top-0 left-0 right-0 z-40 bg-gradient-to-b from-slate-900/95 to-transparent pb-8 px-6 pt-4">
        <div className="flex items-start justify-between">
          <div>
            <h1 className="text-2xl font-bold text-white flex items-center gap-3">
              <span className="w-10 h-10 bg-gradient-to-br from-blue-500/30 to-cyan-600/30 rounded-xl flex items-center justify-center border border-blue-400/30">
                <span className="text-xl">💼</span>
              </span>
              HP Revenue Control
            </h1>
            <p className="text-slate-300 text-sm mt-1">Edge Control Plane • Predictive Impact Analysis • Autonomous Protection</p>
            <div className="mt-2 flex items-center gap-2">
              <span className="text-[10px] px-2 py-1 bg-blue-500/20 text-blue-300 rounded border border-blue-500/30">
                Celerway Edge AI
              </span>
              <span className="text-[10px] px-2 py-1 bg-slate-700/50 text-slate-400 rounded">
                HP Smart Device Services
              </span>
            </div>
          </div>
          <RevenueStatsBar 
            printers={printers} 
            impacts={impacts}
            gateway={gateway}
            onOpenPanel={() => setIsPanelOpen(true)} 
          />
        </div>
      </div>
      
      {/* Proactive Alert */}
      <ProactiveRevenueAlert 
        impacts={impacts} 
        printers={printers} 
        onViewDetails={() => setIsPanelOpen(true)} 
      />
      
      {/* Loading */}
      {loading && (
        <div className="absolute inset-0 z-50 flex items-center justify-center bg-slate-900/90">
          <div className="text-center">
            <div className="w-16 h-16 border-4 border-cyan-400/30 border-t-cyan-400 rounded-full animate-spin mx-auto mb-4" />
            <div className="text-cyan-300 text-lg font-medium">Initializing Control Plane...</div>
            <div className="text-slate-400 text-sm mt-1">Analyzing Revenue Impact</div>
          </div>
        </div>
      )}
      
      {/* React Flow */}
      {!loading && (
        <ReactFlow
          nodes={nodes}
          edges={edges}
          onNodesChange={onNodesChange}
          onEdgesChange={onEdgesChange}
          nodeTypes={nodeTypes}
          fitView
          fitViewOptions={{ padding: 0.2 }}
          minZoom={0.2}
          maxZoom={1.5}
          defaultViewport={{ x: 0, y: 0, zoom: 0.5 }}
          proOptions={{ hideAttribution: true }}
        >
          <Background variant="dots" gap={20} size={1} color="rgba(6, 182, 212, 0.15)" />
          <Controls className="!bg-slate-700/90 !border-slate-600/50 !rounded-xl" />
          <MiniMap 
            nodeColor={(node) => {
              if (node.type === 'printOps') return '#22d3ee';
              if (node.type === 'siteGateway') return '#34d399';
              if (node.data?.revenueStatus === REVENUE_STATUS.CRITICAL) return '#ef4444';
              if (node.data?.revenueStatus === REVENUE_STATUS.AT_RISK) return '#f59e0b';
              if (node.data?.revenueStatus === REVENUE_STATUS.PROTECTED) return '#3b82f6';
              if (node.data?.revenueStatus === REVENUE_STATUS.OFFLINE) return '#64748b';
              return '#34d399';
            }}
            maskColor="rgba(15, 23, 42, 0.8)"
            className="!bg-slate-800/90 !border-slate-600/50 rounded-xl"
          />
          <Panel position="bottom-left"><Legend /></Panel>
        </ReactFlow>
      )}
      
      {/* Revenue Impact Panel */}
      <RevenueImpactPanel 
        isOpen={isPanelOpen} 
        onClose={() => setIsPanelOpen(false)} 
        impacts={impacts}
        printers={printers}
        gateway={gateway}
      />
      
      {/* Animations */}
      <style>{`
        @keyframes slide-in-left { from { opacity: 0; transform: translateX(-20px); } to { opacity: 1; transform: translateX(0); } }
        @keyframes slide-in-right { from { transform: translateX(100%); } to { transform: translateX(0); } }
        .animate-slide-in-left { animation: slide-in-left 0.3s ease-out; }
        .animate-slide-in-right { animation: slide-in-right 0.3s ease-out; }
      `}</style>
    </div>
  );
};

export default HPRevenueControlSentry;
